/**
 * A one-shot proposal, accepted whole, in part, or not at all — Story 27.4.
 *
 * PRD §3.5: "**propose, don't apply.** Every AI turn produces a reviewable diff with accept /
 * accept-partial / discard. Never silent mutation." RULE-020: nothing reaches a draft before a
 * person accepts, and DECISION-167: no funnel row exists before then either — so discarding
 * leaves nothing behind, and accepting is what creates the funnel, with the proposal's archetype.
 *
 * THE PART IS THE STEP, because that is the granularity the drawing gives: `editor-build`'s
 * `proposal` state ticks steps, and "Hot from 70, Review from 45" beneath them is not tickable.
 * So an accept is a sequence of parts, each one `edit()` and so one undo step (RULE-018):
 *
 *   1. the funnel's own — its bands, their outcomes, and anything else no step owns;
 *   2. each ticked step, in the proposal's order, with its questions and what scores them;
 *   3. the rules whose steps were all ticked, in the proposal's order — "Rules run in order. The
 *      first matching rule wins" (`editor-logic`), so they are applied together, last, and never
 *      reordered by which step each happened to name;
 *   4. the person's own description as the ideal customer, when the proposal ends with it
 *      (DECISION-180, Story 27.6) — last of all, so it replaces any profile the model wrote.
 *
 * AN OPERATION NEEDS EVERY STEP IT NAMES. A question names its step; an option, a criterion or an
 * extraction field names its question's; a rule names its own step, the question or fact it reads
 * and the step or question it acts on. One that names a step left out is left out with it —
 * never applied to a funnel missing what it points at. The places are read off the funnel the
 * server built (`proposal.funnel`), where every id already resolves.
 *
 * THE ORDER IS THE PROPOSAL'S FINISHED ORDER, not the order the operations were written in. Steps,
 * each step's questions and the rules are applied in the order `proposal.funnel` holds them, with
 * any `position` dropped, so appending reproduces exactly the funnel the review showed — minus
 * what was left out.
 *
 * `simulate` applies the chosen parts to the blank funnel the server starts from (`blankFunnel`,
 * shared since this story) and returns what the save would say, so the review can refuse a choice
 * before anything is created rather than the autosave refusing it after (27.2's note).
 */
import {
  applyOperation,
  applyOperations,
  blankFunnel,
  type Archetype,
  type Funnel,
  type Operation,
  type SequenceOutcome,
} from '@leadgible/schema'
import { COMPOSED, DRAWN } from './proposal-copy'
import { edit } from './store'

/** What `POST /v1/w/{wid}/proposals` answers with (Story 27.3). */
export type Proposal = {
  readonly archetype: Archetype
  readonly operations: readonly Operation[]
  /** The operations applied to the blank funnel, as the server checked them. */
  readonly funnel: Funnel
  readonly cached: boolean
}

/** One part of an accept: `edit()`'s label, and the operations its recipe applies in order. */
export type AcceptPart = {
  readonly label: string
  readonly operations: readonly Operation[]
}

/** The name accepting creates the funnel with — Q-138's conservative default, the drawn header's. */
export const UNTITLED = 'Untitled funnel'

type IcpOperation = Extract<Operation, { op: 'set_icp_profile' }>

/**
 * The operation the server composed from the person's own words — the proposal's last, when it
 * is a `set_icp_profile` (Story 27.6). Null for a proposal that does not end with one.
 */
export function personsProfile(proposal: Proposal): IcpOperation | null {
  const last = proposal.operations.at(-1)
  return last !== undefined && last.op === 'set_icp_profile' ? last : null
}

/** Where every block, field and criterion of the finished funnel lives. */
type Places = {
  readonly stepOfBlock: ReadonlyMap<string, string>
  readonly blockOfField: ReadonlyMap<string, string>
  readonly blockOfCriterion: ReadonlyMap<string, string>
}

function placesIn(funnel: Funnel): Places {
  const stepOfBlock = new Map<string, string>()
  for (const step of funnel.steps) for (const blockId of step.blocks) stepOfBlock.set(blockId, step.id)
  const blockOfField = new Map(funnel.rubric.extraction.map((field) => [field.field_key, field.block_id] as const))
  const blockOfCriterion = new Map<string, string>()
  for (const criterion of funnel.rubric.criteria) {
    const blockId =
      criterion.source === 'answer_ref'
        ? criterion.block_id
        : criterion.source === 'extracted'
          ? blockOfField.get(criterion.field_key)
          : undefined
    if (blockId !== undefined) blockOfCriterion.set(criterion.id, blockId)
  }
  return { stepOfBlock, blockOfField, blockOfCriterion }
}

/**
 * The steps an operation cannot be applied without. Empty for an operation no step owns: a band,
 * an outcome, a routing action, a persona, the ideal customer, and a criterion reading something
 * about the whole submission (`derived`, `metadata`).
 */
function stepsNamedBy(operation: Operation, places: Places): string[] {
  const ofBlock = (blockId: string | undefined): string[] => {
    const stepId = blockId === undefined ? undefined : places.stepOfBlock.get(blockId)
    return stepId === undefined ? [] : [stepId]
  }
  const ofField = (fieldKey: string | undefined): string[] =>
    ofBlock(fieldKey === undefined ? undefined : places.blockOfField.get(fieldKey))

  switch (operation.op) {
    case 'add_step':
      return [operation.id]
    case 'add_question':
      return [operation.step_id]
    case 'set_option':
    case 'add_extraction_field':
      return ofBlock(operation.block_id)
    case 'set_criterion':
      if (operation.block_id !== undefined) return ofBlock(operation.block_id)
      if (operation.field_key !== undefined) return ofField(operation.field_key)
      return operation.id !== undefined ? ofBlock(places.blockOfCriterion.get(operation.id)) : []
    case 'add_branch': {
      const subject = operation.when.subject
      const effect = operation.then
      return [
        ...(operation.step_id !== undefined ? [operation.step_id] : []),
        ...(subject.kind === 'answer' ? ofBlock(subject.block_id) : subject.kind === 'extracted' ? ofField(subject.field_key) : []),
        ...('target_block_id' in effect ? ofBlock(effect.target_block_id) : []),
        ...('target_step_id' in effect ? [effect.target_step_id] : []),
      ]
    }
    case 'set_band':
    case 'set_outcome':
    case 'set_persona':
    case 'add_action':
    case 'set_icp_profile':
      return []
  }
}

/** An operation without the `position` it was written with — the finished order is applied instead. */
function unplaced(operation: Operation): Operation {
  if (operation.op !== 'add_step' && operation.op !== 'add_question' && operation.op !== 'add_branch') return operation
  const { position: _written, ...rest } = operation
  return rest as Operation
}

/** A step as the review lists it, with the operations that arrive with it. */
export type ProposedStep = {
  readonly id: string
  /** From 1, in the proposal's order — the drawing's "STEP 1". */
  readonly number: number
  readonly title: string
  /** Its `add_step`, its questions in their finished order, then what configures and scores them. */
  readonly operations: readonly Operation[]
}

/** The proposal cut into its parts — what the review ticks, and what accepting applies. */
export type ProposalParts = {
  /** Every operation no step owns and that is not a rule, in the proposal's order. */
  readonly funnel: readonly Operation[]
  readonly steps: readonly ProposedStep[]
  /** Every rule, in the order the finished funnel runs them. */
  readonly rules: readonly Operation[]
  /** The person's own words, last (Story 27.6); null when the proposal carries none. */
  readonly customer: IcpOperation | null
}

export function partsOf(proposal: Proposal): ProposalParts {
  const { funnel } = proposal
  const places = placesIn(funnel)
  const stepIndex = new Map(funnel.steps.map((step, index) => [step.id, index] as const))
  const blockIndex = new Map(funnel.steps.flatMap((step) => step.blocks.map((blockId, index) => [blockId, index] as const)))
  const ruleIndex = new Map(funnel.logic.map((rule, index) => [rule.id, index] as const))
  const customer = personsProfile(proposal)
  const written = customer === null ? proposal.operations : proposal.operations.slice(0, -1)

  const own: Operation[] = []
  const rules: Operation[] = []
  const byStep = new Map<string, { opening: Operation[]; questions: Operation[]; rest: Operation[] }>(
    funnel.steps.map((step) => [step.id, { opening: [], questions: [], rest: [] }]),
  )
  for (const operation of written) {
    if (operation.op === 'add_branch') {
      rules.push(operation)
      continue
    }
    const named = stepsNamedBy(operation, places).filter((stepId) => stepIndex.has(stepId))
    if (named.length === 0) {
      own.push(operation)
      continue
    }
    const latest = named.reduce((a, b) => (stepIndex.get(a)! >= stepIndex.get(b)! ? a : b))
    const slot = byStep.get(latest)!
    if (operation.op === 'add_step') slot.opening.push(operation)
    else if (operation.op === 'add_question') slot.questions.push(operation)
    else slot.rest.push(operation)
  }

  const inFinishedOrder = (index: ReadonlyMap<string, number>, idOf: (operation: Operation) => string) =>
    (a: Operation, b: Operation) => (index.get(idOf(a)) ?? 0) - (index.get(idOf(b)) ?? 0)
  const questionId = (operation: Operation) => (operation.op === 'add_question' ? operation.block.id : '')
  const ruleId = (operation: Operation) => (operation.op === 'add_branch' ? operation.id : '')

  return {
    funnel: own,
    steps: funnel.steps.map((step, index) => {
      const slot = byStep.get(step.id)!
      return {
        id: step.id,
        number: index + 1,
        title: step.title ?? `Step ${index + 1}`,
        operations: [
          ...slot.opening.map(unplaced),
          ...[...slot.questions].sort(inFinishedOrder(blockIndex, questionId)).map(unplaced),
          ...slot.rest,
        ],
      }
    }),
    rules: [...rules].sort(inFinishedOrder(ruleIndex, ruleId)).map(unplaced),
    customer,
  }
}

/** The labels `edit()` records — "Accept step 2: Budget" undoes as what it did. */
export const PART_LABEL = {
  funnel: 'Accept the bands and outcomes',
  step: (step: ProposedStep) => `Accept step ${step.number}: ${step.title}`,
  rules: 'Accept the rules',
  customer: 'Accept the ideal customer',
} as const

/**
 * The parts accepting applies, in order, for the steps ticked. An operation that names a step left
 * out is left out with it, wherever in the proposal it was written; a part left empty is no part.
 */
export function planFor(proposal: Proposal, ticked: ReadonlySet<string>): AcceptPart[] {
  const parts = partsOf(proposal)
  const places = placesIn(proposal.funnel)
  const included = (operation: Operation) => stepsNamedBy(operation, places).every((stepId) => ticked.has(stepId))
  const plan: AcceptPart[] = []
  const add = (label: string, operations: readonly Operation[]) => {
    if (operations.length > 0) plan.push({ label, operations })
  }
  add(PART_LABEL.funnel, parts.funnel)
  for (const step of parts.steps) if (ticked.has(step.id)) add(PART_LABEL.step(step), step.operations.filter(included))
  add(PART_LABEL.rules, parts.rules.filter(included))
  if (parts.customer !== null) add(PART_LABEL.customer, [parts.customer])
  return plan
}

/**
 * The chosen parts applied to the blank funnel the server starts from — what the save would say,
 * before anything is created. The ids are placeholders: nothing reads them but the parse.
 */
export function simulate(proposal: Proposal, plan: readonly AcceptPart[]): SequenceOutcome {
  const blank = blankFunnel({
    id: 'fnl_proposal',
    orgId: proposal.funnel.meta.org_id,
    workspaceId: proposal.funnel.meta.workspace_id,
    name: UNTITLED,
    archetype: proposal.archetype,
  })
  return applyOperations(blank, plan.flatMap((part) => part.operations))
}

/**
 * "Hot from 70, Review from 45 — the bands every template starts with." — every band above the
 * lowest, from the top. The drawn suffix is said only when the ladder IS the templates'
 * (`TEMPLATE_LADDER`); a model's own bands get the list alone. Null for a single band, which has
 * nothing above it to name.
 */
export function bandsLine(funnel: Funnel): string | null {
  const above = [...funnel.rubric.bands].sort((a, b) => b.min - a.min).slice(0, -1)
  if (above.length === 0) return null
  const list = above.map((band) => `${band.label} from ${band.min}`).join(', ')
  return isTemplateLadder(funnel) ? DRAWN.bandsLikeTemplates(list) : COMPOSED.bandsOwn(list)
}

/**
 * The ladder the three curated blueprints start with — migration 0009's, and
 * `tests/proposal-copy.test.ts` reads that migration to hold it there.
 */
export const TEMPLATE_LADDER = [
  { label: 'Nurture', min: 0, max: 44, tone: 'nurture', is_qualified: false },
  { label: 'Review', min: 45, max: 69, tone: 'review', is_qualified: false },
  { label: 'Hot', min: 70, max: 100, tone: 'hot', is_qualified: true },
] as const

function isTemplateLadder(funnel: Funnel): boolean {
  const ordered = [...funnel.rubric.bands].sort((a, b) => a.min - b.min)
  return (
    ordered.length === TEMPLATE_LADDER.length &&
    ordered.every((band, index) => {
      const template = TEMPLATE_LADDER[index]!
      return (
        band.label === template.label &&
        band.min === template.min &&
        band.max === template.max &&
        band.tone === template.tone &&
        band.is_qualified === template.is_qualified
      )
    })
  )
}

/** A part the vocabulary refused while it was being accepted. */
class PartRefused extends Error {}

/**
 * The accept itself, into the funnel the editor has just loaded: one `edit()` per part, so each
 * undoes as its own step (RULE-018), and every change is the vocabulary's (RULE-019). A part an
 * operation refuses is thrown out of its own recipe, so Immer keeps none of it, and nothing after
 * it is applied — `simulate` rules that out for the plan, so it would mean the funnel loaded is
 * not the funnel simulated. Returns whether every part went in.
 */
export function acceptInto(plan: readonly AcceptPart[]): boolean {
  for (const part of plan) {
    try {
      edit(part.label, (draft) => {
        for (const operation of part.operations) {
          if (!applyOperation(draft, operation).ok) throw new PartRefused()
        }
      })
    } catch (thrown) {
      if (thrown instanceof PartRefused) return false
      throw thrown
    }
  }
  return true
}
